import {
  eliteRewards,
  hiveRewards,
  hiveTemplate,
  packRewards,
  packTemplate,
  worldBossTemplate,
  zombieById,
  itemById,
  VAULT_PACK_LEVEL,
} from "@zend/content";
import { pick, useT } from "../i18n/index.js";
import { GameIcon, ResourceIcon, StatIcon } from "../assets/gameIcons.js";
import type { Resource } from "@zend/content";
import { formatCompact } from "../ui/Counter.js";
import type { WorldTile } from "./types.js";

/** Public PvE composition uses the same templates/elite offset as march resolution. Player garrisons stay private. */
export function TargetIntel({ tile }: { tile: WorldTile }) {
  const t = useT();
  if (!["pack", "elite", "hive", "vault", "boss"].includes(tile.kind)) return null;
  const pack =
    tile.kind === "hive"
      ? hiveTemplate(tile.level)
      : tile.kind === "boss"
        ? worldBossTemplate()
        : packTemplate(
            tile.kind === "vault"
              ? VAULT_PACK_LEVEL
              : tile.kind === "elite"
                ? Math.min(40, tile.level + 3)
                : tile.level,
          );
  const reward =
    tile.kind === "elite"
      ? eliteRewards(tile.level)
      : tile.kind === "hive"
        ? hiveRewards(tile.level)
        : tile.kind === "vault"
          ? {
              credits: 20,
              items: [
                { itemId: "itm.cdc", qty: 40 },
                { itemId: "itm.ufr_s", qty: 8 },
                { itemId: "itm.token_salvage", qty: 40 },
              ],
            }
          : tile.kind === "boss"
            ? null
            : packRewards(tile.level);
  const hp = tile.hive?.hpPct ?? tile.boss?.hpPct ?? 100;
  return (
    <section className="target-intel" data-testid="target-intel">
      <header>
        <h3>
          {pack.members.length} {pick({ de: "Gegner", en: "enemies" })}
        </h3>
        <strong>
          <StatIcon stat="power" size={16} /> {formatCompact(pack.power)} · {Math.round(hp)}% HP
        </strong>
      </header>
      <div className="intel-enemies">
        {pack.members.map((member, index) => {
          const def = zombieById(member.zombieId);
          return (
            <article key={index} data-enemy-id={member.zombieId} data-enemy-level={member.level}>
              <GameIcon id={member.zombieId} size={120} label={pick(def.name)} />
              <b className="enemy-level">Lv {member.level}</b>
              <strong>{pick(def.name)}</strong>
            </article>
          );
        })}
      </div>
      <div className="intel-loot">
        <strong>{pick({ de: "Beute", en: "Loot" })}</strong>
        {reward ? (
          <div className="intel-rewards">
            {Object.entries("resources" in reward ? (reward.resources ?? {}) : {})
              .filter(([, qty]) => Number(qty) > 0)
              .map(([key, qty]) => (
                <span key={key} title={t(`res.${key}`)}>
                  <ResourceIcon resource={key as Resource} size={38} label={t(`res.${key}`)} />
                  <b>{formatCompact(Number(qty))}</b>
                </span>
              ))}
            {reward.items?.map((item) => (
              <span key={item.itemId} title={pick(itemById(item.itemId).name)}>
                <GameIcon id={item.itemId} size={38} label={pick(itemById(item.itemId).name)} />
                <b>×{item.qty}</b>
              </span>
            ))}
            {"credits" in reward && reward.credits ? (
              <span>
                <ResourceIcon resource="CREDITS" size={38} />
                <b>{reward.credits}</b>
              </span>
            ) : null}
          </div>
        ) : (
          <span>{pick({ de: "Rangbelohnung", en: "Rank reward" })}</span>
        )}
      </div>
      <details className="field-details">
        <summary>{pick({ de: "Gegner & Beutedetails", en: "Enemy & loot details" })}</summary>
        <p>
          {pick({
            de: "Bedrohung ist ein Richtwert. Rollen, Ausrüstung und HP beeinflussen den Ausgang.",
            en: "Threat is a guide. Roles, equipment and HP affect the outcome.",
          })}
        </p>
        {pack.members.map((member, index) => (
          <p key={index}>
            {pick(zombieById(member.zombieId).name)} · Lv {member.level} ·{" "}
            {t(`role.${zombieById(member.zombieId).roleAnalogue}`)}
          </p>
        ))}
        {reward?.items?.map((item) => (
          <p key={item.itemId}>
            {pick(itemById(item.itemId).name)} ×{item.qty}
          </p>
        ))}
        {tile.kind === "pack" ? (
          <p>
            {packRewards(tile.level).cdcChancePct}%{" "}
            {pick({
              de: "Zusatzchance auf Kommandantendaten. Siege zählen zur täglichen Jagd.",
              en: "extra chance for commander data. Wins count toward the daily hunt.",
            })}
          </p>
        ) : null}
        {tile.kind === "boss" ? (
          <p>
            {pick({
              de: "Schaden zählt für die Rangliste. Belohnungen folgen nach dem Bossfenster und sind nicht garantiert.",
              en: "Damage counts toward ranking. Rewards follow the boss window and are not guaranteed.",
            })}
          </p>
        ) : null}
      </details>
    </section>
  );
}
